import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal } from 'antd';

import { CompensationStatus, Namespace } from 'enums';
import { CompensationStatusItem } from 'components/common';
import { Compensation, EditableCompensation } from 'types';

import styles from './EditCompensationStatusModal.module.scss';

interface Props {
  readonly nextStatus?: CompensationStatus;
  readonly compensation: Compensation;

  readonly submit: (compensation: EditableCompensation) => Promise<void>;
  readonly close: () => void;
}

export const EditCompensationStatusModal = ({ nextStatus, compensation, submit, close }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const reviewerComment = Form.useWatch('reviewerComment', form);

  useEffect(() => form.setFieldsValue(compensation), [form, compensation]);

  const onOk = useCallback(() => {
    setLoading(true);
    submit({
      ...compensation,
      targetPeriods: [compensation.targetPeriod],
      status: nextStatus!,
      reviewerComment,
    }).then(() => {
      setLoading(false);
      close();
    });
  }, [submit, close, compensation, nextStatus, reviewerComment]);

  return (
    <Modal
      title={t(`${Namespace.StatusActions}:${Namespace.Long}:${nextStatus}`)}
      open={!!nextStatus}
      okText={t('save')}
      onOk={onOk}
      onCancel={close}
      confirmLoading={loading}
      cancelButtonProps={{ style: { display: 'none' } }}
      destroyOnHidden
    >
      <Form layout="vertical" form={form}>
        <div className={styles.statusContainer}>
          <span className={styles.text}>{t('status')}</span>
          <CompensationStatusItem status={nextStatus!} />
        </div>
        <Form.Item name="reviewerComment" label={t('reviewerComment')}>
          <Input.TextArea
            placeholder={t('reviewerCommentPlaceholder', { ns: Namespace.CompensationForm })}
            rows={7}
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
